/**
 * DigestPreferencePanel — lets a vendor choose how often they receive the
 * weekly digest email (Weekly / Monthly / Off). No password gate — it's a
 * comms preference, not a payout setting.
 */
import React, { useEffect, useState } from 'react';
import { Mail, Loader2, Check } from 'lucide-react';
import { vendorAdminApi } from '../services/api';
import { toast } from 'sonner';

const OPTIONS = [
  {
    value: 'weekly',
    title: 'Weekly',
    subtitle: 'Every Monday morning',
    hint: 'Recommended — stay on top of trends.',
  },
  {
    value: 'monthly',
    title: 'Monthly',
    subtitle: 'First Monday of each month',
    hint: 'Just the highlights, less inbox noise.',
  },
  {
    value: 'off',
    title: 'Off',
    subtitle: 'No digest emails',
    hint: 'You can still check stats from your dashboard.',
  },
];

const fmtDate = (iso) => {
  if (!iso) return 'never sent yet';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return 'never sent yet'; }
};

const DigestPreferencePanel = ({ vendorId, impersonating }) => {
  const vid = impersonating ? vendorId : undefined;
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null); // stores the option being saved

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await vendorAdminApi.getDigestPreference(vid);
        if (alive) setData(r);
      } catch {
        if (alive) setData({ frequency: 'weekly', last_sent_at: null, recipient_email: null });
      }
    })();
    return () => { alive = false; };
  }, [vid]);

  const choose = async (freq) => {
    if (!data || data.frequency === freq || saving) return;
    setSaving(freq);
    try {
      const r = await vendorAdminApi.setDigestPreference(freq, vid);
      setData((d) => ({ ...d, frequency: r.frequency }));
      const label = OPTIONS.find((o) => o.value === freq)?.title || freq;
      toast.success(freq === 'off'
        ? 'Digest emails turned off.'
        : `Digest set to ${label.toLowerCase()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save preference');
    } finally {
      setSaving(null);
    }
  };

  if (!data) {
    return (
      <section
        className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-2"
        data-testid="digest-preference-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Loading digest preferences…</span>
      </section>
    );
  }

  return (
    <section
      className="mt-6 bg-white border border-slate-200 rounded-2xl p-5"
      data-testid="digest-preference-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-5 w-5 text-emerald-600" />
        <h2 className="font-semibold text-slate-900">Weekly digest email</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        A short summary of your orders, revenue and preferred-driver reliability. Sent to{' '}
        <span className="font-medium text-slate-700" data-testid="digest-preference-recipient">
          {data.recipient_email || 'your payout email (set one under Stripe payments)'}
        </span>.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {OPTIONS.map((opt) => {
          const active = data.frequency === opt.value;
          const isBusy = saving === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => choose(opt.value)}
              disabled={saving !== null}
              data-testid={`digest-preference-option-${opt.value}`}
              aria-pressed={active}
              className={`relative text-left rounded-xl border p-4 transition-all
                ${active
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}
                ${saving && !isBusy ? 'opacity-60 cursor-wait' : ''}
                disabled:cursor-not-allowed`}
            >
              {active && (
                <span
                  className="absolute top-3 right-3 h-5 w-5 rounded-full bg-emerald-600 text-white flex items-center justify-center"
                  aria-hidden
                >
                  {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                </span>
              )}
              {!active && isBusy && (
                <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-slate-400" />
              )}
              <div className={`font-semibold ${active ? 'text-emerald-800' : 'text-slate-900'}`}>
                {opt.title}
              </div>
              <div className={`text-xs mt-0.5 ${active ? 'text-emerald-700' : 'text-slate-500'}`}>
                {opt.subtitle}
              </div>
              <div className="text-[11px] text-slate-500 mt-2 leading-snug">
                {opt.hint}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 text-[11px] text-slate-400" data-testid="digest-preference-last-sent">
        Last digest email: {fmtDate(data.last_sent_at)}
      </div>
    </section>
  );
};

export default DigestPreferencePanel;
